import { getAllInvalidFieldNames } from '../../../../../helpers/validation-helpers';
import { PERSIST_DATA_STATES, STATE_IDS } from '../constants/states';
import { ALWAYS_MANDATORY_TOOLS, TOOL_IDS, MIN_CLIENT_AGE } from '../constants/general';
import { assessmentMethodsSchema, visualSchema } from './validationSchema';

import type {
  ValidateInitialAssessmentsTools,
  ValidateAssessmentTool,
  GetSavedVisualErrorCount,
  ValidateToolsData,
  GetMissingMandatoryTools,
} from './types/validations';
import type { ToolId } from '../constants/types/general';

const validateAssessmentTool: ValidateAssessmentTool = (
  tool,
  tpPersistedData,
  assessmentVersion,
) => {
  const persistedDataForCurrentTool =
    tpPersistedData?.[`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${tool?.id}`];
  const toolDataToValidate = persistedDataForCurrentTool
    ? { ...persistedDataForCurrentTool }
    : { ...tool };
  delete toolDataToValidate.id;
  delete toolDataToValidate.visuals;
  return getAllInvalidFieldNames(assessmentMethodsSchema, {
    assessment_version: assessmentVersion,
    ...toolDataToValidate,
  });
};

const getSavedVisualErrorCount: GetSavedVisualErrorCount = (tool, tpPersistedData) => {
  return (
    tool.visuals?.reduce((savedVisualCount: number, visual: any) => {
      let errors = 0;
      const persistedCurrentVisual =
        tpPersistedData?.[`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${tool.id}-${visual.id}`];
      const data = persistedCurrentVisual ? persistedCurrentVisual : visual;
      const err = getAllInvalidFieldNames(visualSchema, {
        label: data.label,
        description: data.description,
      });
      errors = Object.keys(err).length;
      return savedVisualCount + errors;
    }, 0) || 0
  );
};

// validateToolsData, validateAssessmentTool, getSavedVisualErrorCount moved here to use in
export const validateToolsData: ValidateToolsData = (tools, tpPersistedData, assessmentVersion) => {
  let toolsErrorCount = tools.reduce((count, tool) => {
    let persistedVisualErrorCounts = 0;
    let savedVisualErrorCount = 0;
    let savedToolsErrorCount = 0;
    if (tpPersistedData) {
      // If persisted data exists for tool(current iteration) validate it
      const savedToolErrors = validateAssessmentTool(tool, tpPersistedData, assessmentVersion);
      savedToolsErrorCount = Object.keys(savedToolErrors).length;

      // If new unsaved visual exists for this tool validate it
      const persistedNewVisual =
        tpPersistedData[`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${tool.id}-new`];
      if (persistedNewVisual) {
        const err = getAllInvalidFieldNames(visualSchema, {
          label: persistedNewVisual.label,
          description: persistedNewVisual.description,
        });
        persistedVisualErrorCounts += Object.keys(err).length;
      }

      // calculate saved visual error count
      savedVisualErrorCount = getSavedVisualErrorCount(tool, tpPersistedData);
    }
    if (!tool.visuals?.length && persistedVisualErrorCounts === 0) {
      savedVisualErrorCount++;
    }
    return count + persistedVisualErrorCounts + savedVisualErrorCount + savedToolsErrorCount;
  }, 0);

  // If new unsaved tool exists validate it
  const persistedNewTool =
    tpPersistedData?.[`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`];
  if (persistedNewTool) {
    const err = getAllInvalidFieldNames(assessmentMethodsSchema, {
      ...persistedNewTool,
      assessment_version: assessmentVersion,
    });
    toolsErrorCount += Object.keys(err).length;
  }
  return toolsErrorCount;
};

export const getMissingRecommendedTools: GetMissingMandatoryTools = (tools, clientAge) => {
  const toolNames = tools.map((tool) => tool.tool_name);
  const missingTools: ToolId[] = [];

  if (clientAge < MIN_CLIENT_AGE) {
    if (!toolNames.includes(TOOL_IDS.VB_MAPP)) {
      missingTools.push(TOOL_IDS.VB_MAPP);
    }
  } else {
    if (!toolNames.includes(TOOL_IDS.AFLS)) {
      missingTools.push(TOOL_IDS.AFLS);
    }
  }

  ALWAYS_MANDATORY_TOOLS.forEach((tool) => {
    if (!toolNames.includes(tool)) {
      missingTools.push(tool);
    }
  });

  return missingTools;
};

export const validateInitialAssesmentsTools: ValidateInitialAssessmentsTools = (
  initialAssessment,
  tools,
  persistDataState,
  assessmentVersion,
  clientAge,
) => {
  let errorCount = 0;
  // Validate if user has added at least one tool (Assessment Method section)
  // also validate if a persisted tool data exists, if yes, validate it.
  // Check if all tools has at least one visual and if persisted data exists
  // for any visual then validate persisted data
  const persistedNewTool =
    persistDataState?.[`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`];
  if (initialAssessment?.id) {
    errorCount +=
      tools.length > 0 || persistedNewTool
        ? validateToolsData(tools, persistDataState, assessmentVersion)
        : 1;
  }

  return {
    assessmentMethodsErrorCount: errorCount,
    missingRecommendedTools: getMissingRecommendedTools(tools, clientAge),
  };
};
